import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { CheckCircle, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";

const MESSENGERS = [
    { value: "telegram", label: "Telegram" },
    { value: "whatsapp", label: "WhatsApp" },
    { value: "max", label: "MAX" },
    { value: "call", label: "Обычный звонок" },
    { value: "other", label: "Другой" },
];

const formatPhone = (value) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length === 0) return "";
    let r = "+";
    if (digits.length >= 1) r += digits[0];
    if (digits.length >= 2) r += " (" + digits.slice(1, Math.min(4, digits.length));
    if (digits.length >= 4) r += ") " + digits.slice(4, Math.min(7, digits.length));
    if (digits.length >= 7) r += "-" + digits.slice(7, Math.min(9, digits.length));
    if (digits.length >= 9) r += "-" + digits.slice(9, Math.min(11, digits.length));
    return r;
};

export default function RequestForm({ source = "contact", carData = {}, configuratorData = {}, onSuccess }) {
    const [form, setForm] = useState({ name: "", phone: "", messenger: "", messenger_other: "" });
    const [loading, setLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [errors, setErrors] = useState({});

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Введите имя";
        if (!form.phone || form.phone.replace(/[^0-9]/g, "").length < 11) e.phone = "Введите корректный номер";
        if (!form.messenger) e.messenger = "Выберите мессенджер";
        if (form.messenger === "other" && !form.messenger_other.trim()) e.messenger_other = "Укажите способ связи";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);
        try {
            await api.post("/applications", {
                ...form,
                source,
                car_id: carData.id || "",
                car_brand: carData.brand || "",
                car_model: carData.model || "",
                configurator_data: configuratorData,
            });
            setShowSuccess(true);
            setForm({ name: "", phone: "", messenger: "", messenger_other: "" });
            onSuccess?.();
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="request-form">
                <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider">Имя *</Label>
                    <Input
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Ваше имя"
                        className="mt-1 bg-black/50 border-white/10 focus:border-[#0099FF] text-white h-12 rounded-none"
                        data-testid="request-name-input"
                    />
                    {errors.name && <p className="text-[#E31E24] text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider">Телефон *</Label>
                    <Input
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
                        placeholder="+7 (___) ___-__-__"
                        className="mt-1 bg-black/50 border-white/10 focus:border-[#0099FF] text-white h-12 rounded-none"
                        data-testid="request-phone-input"
                        maxLength={18}
                    />
                    {errors.phone && <p className="text-[#E31E24] text-xs mt-1">{errors.phone}</p>}
                </div>
                <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-wider mb-3 block">Удобный мессенджер *</Label>
                    <RadioGroup
                        value={form.messenger}
                        onValueChange={(v) => setForm({ ...form, messenger: v })}
                        className="grid grid-cols-2 gap-2"
                        data-testid="request-messenger-radio"
                    >
                        {MESSENGERS.map((m) => (
                            <div
                                key={m.value}
                                className={`flex items-center gap-2 p-3 border transition-all cursor-pointer ${
                                    form.messenger === m.value
                                        ? "border-[#0099FF] bg-[#0099FF]/10"
                                        : "border-white/10 bg-black/30"
                                }`}
                                onClick={() => setForm({ ...form, messenger: m.value })}
                            >
                                <RadioGroupItem value={m.value} id={`msg-${m.value}`} />
                                <Label htmlFor={`msg-${m.value}`} className="text-sm text-white cursor-pointer">
                                    {m.label}
                                </Label>
                            </div>
                        ))}
                    </RadioGroup>
                    {errors.messenger && <p className="text-[#E31E24] text-xs mt-1">{errors.messenger}</p>}
                </div>
                {form.messenger === "other" && (
                    <div>
                        <Input
                            value={form.messenger_other}
                            onChange={(e) => setForm({ ...form, messenger_other: e.target.value })}
                            placeholder="Укажите способ связи"
                            className="bg-black/50 border-white/10 focus:border-[#0099FF] text-white h-12 rounded-none"
                            data-testid="request-messenger-other-input"
                        />
                        {errors.messenger_other && <p className="text-[#E31E24] text-xs mt-1">{errors.messenger_other}</p>}
                    </div>
                )}
                <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#0099FF] text-white h-12 uppercase tracking-wider font-bold hover:bg-[#007acc] hover:shadow-[0_0_15px_rgba(0,153,255,0.5)] transition-all rounded-none"
                    data-testid="request-submit-btn"
                >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Отправить заявку"}
                </Button>
            </form>

            <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
                <DialogContent className="bg-[#09090b] border-white/10 text-center max-w-md" data-testid="success-modal">
                    <DialogHeader>
                        <DialogTitle className="sr-only">Заявка отправлена</DialogTitle>
                    </DialogHeader>
                    <div className="py-8">
                        <CheckCircle className="w-16 h-16 text-[#0099FF] mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-white mb-2 font-display">Спасибо!</h3>
                        <p className="text-zinc-400">
                            Ваша заявка успешно отправлена. Наш менеджер свяжется с вами в ближайшее время.
                        </p>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
